/**
 * การแสดงผลจำนวนเงิน
 *
 * ★ เงินในระบบเป็นจำนวนเต็มสตางค์เสมอ ★ ไฟล์นี้เป็นที่เดียวที่แปลงเป็นข้อความ
 * ห้ามมี `/100` กระจายอยู่ตามหน้าจอ — นั่นคือจุดที่เศษสตางค์เริ่มหาย
 *
 * ไม่มีการปัดเศษที่นี่เลย เพราะยอดที่ส่งมาถูกปัดมาแล้วโดย @sushimai/core
 * ถ้าหน้าจอปัดซ้ำ ตัวเลขบนจอกับตัวเลขในฐานจะไม่ตรงกัน
 */

/** 1,234.50 — สำหรับยอดที่ต้องอ่านละเอียด เช่น ยอดบิลและเงินทอน */
export function baht(satang: number): string {
  const negative = satang < 0;
  const abs = Math.abs(satang);
  const whole = Math.floor(abs / 100);
  const fraction = abs % 100;
  const text = `${whole.toLocaleString('en-US')}.${String(fraction).padStart(2, '0')}`;
  return negative ? `-${text}` : text;
}

/** 1,234 — สำหรับปุ่มและป้ายที่พื้นที่จำกัด เศษสตางค์ที่เป็น .00 ไม่ต้องแสดง */
export function bahtShort(satang: number): string {
  return satang % 100 === 0
    ? Math.trunc(satang / 100).toLocaleString('en-US')
    : baht(satang);
}

/** อ่านตัวเลขบาทที่พนักงานพิมพ์ แล้วแปลงเป็นสตางค์ — คืน null ถ้าอ่านไม่ออก */
export function parseBaht(text: string): number | null {
  const trimmed = text.trim().replace(/,/g, '');
  if (trimmed === '' || !/^\d*\.?\d*$/.test(trimmed)) return null;

  const [whole = '', fraction = ''] = trimmed.split('.');
  if (fraction.length > 2) return null;

  // คูณด้วยการต่อสตริง ไม่ใช่ parseFloat(x)*100 — 19.99*100 ได้ 1998.9999999999998
  const satang = Number(`${whole || '0'}${fraction.padEnd(2, '0')}`);
  return Number.isSafeInteger(satang) ? satang : null;
}

/** 08:45 จากค่า timestamp ที่ API ส่งมา */
export function clock(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** 29/08/2569 — ปีพุทธศักราชแบบที่ใบเสร็จร้านไทยใช้ */
export function thaiDate(businessDate: string): string {
  const [y = '', m = '', d = ''] = businessDate.split('-');
  return `${d}/${m}/${Number(y) + 543}`;
}

/**
 * วันทำการของวันนี้ในรูปแบบ YYYY-MM-DD ตามเวลาประเทศไทย
 *
 * ★ ห้ามใช้ toISOString() ★ มันแปลงเป็น UTC ก่อน — ตอนตีหนึ่งของไทย
 * จะได้วันที่ของเมื่อวาน ซึ่งเป็นบั๊กวันเลื่อนแบบเดียวกับที่ระบบเดิมเป็น
 */
export function todayLocal(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}
